import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { AccountPicker, TaxRatePicker } from "@/components/Pickers";
import { type DocumentLineDraft, toMinor } from "./productDetails";

/** Shared document line editor: account, description, quantity, unit, unit
 * price, an optional tax rate and the authoritative amount, plus a SKU under
 * an optional disclosure. Quantity and unit price are reference details only —
 * changing them never derives or replaces the entered amount.
 *
 * Credit-note and supplier-credit editors render this with `showTax={false}`
 * because their line contracts carry no tax. */
export function DocumentLineRow({
  idPrefix,
  index,
  line,
  onChange,
  onRemove,
  canRemove,
  showTax = true,
  accountLabel = "Account",
  disabled = false,
}: {
  idPrefix: string;
  index: number;
  line: DocumentLineDraft;
  onChange: (patch: Partial<DocumentLineDraft>) => void;
  onRemove: () => void;
  canRemove: boolean;
  showTax?: boolean;
  accountLabel?: string;
  disabled?: boolean;
}) {
  const quantityText = (line.quantity ?? "").trim();
  const unitPriceText = (line.unit_price ?? "").trim();
  const quantity = Number(quantityText);
  const unitPrice = Number(unitPriceText);
  const enteredAmount = toMinor(line.amount);
  const computedAmount =
    quantityText &&
    unitPriceText &&
    Number.isFinite(quantity) &&
    quantity > 0 &&
    Number.isFinite(unitPrice) &&
    unitPrice >= 0
      ? Math.round(quantity * unitPrice * 100)
      : null;
  const mismatch =
    enteredAmount !== null &&
    computedAmount !== null &&
    Number.isSafeInteger(computedAmount) &&
    computedAmount !== enteredAmount;

  return (
    <div className="surface grid gap-2 rounded-xl border border-[var(--border)] p-3">
      <div className="grid gap-2 sm:grid-cols-2 sm:items-end lg:grid-cols-4">
        <label
          htmlFor={`${idPrefix}-account`}
          className="grid gap-1 text-xs font-medium lg:col-span-2"
        >
          {accountLabel}
          <AccountPicker
            id={`${idPrefix}-account`}
            value={line.account_id}
            onChange={(value) => onChange({ account_id: value })}
            disabled={disabled}
            required
            ariaLabel={`${accountLabel} for line ${index + 1}`}
          />
        </label>
        <label
          htmlFor={`${idPrefix}-description`}
          className="grid gap-1 text-xs font-medium lg:col-span-2"
        >
          Description
          <Input
            id={`${idPrefix}-description`}
            value={line.description}
            onChange={(event) => onChange({ description: event.target.value })}
            placeholder="Description"
            required
            disabled={disabled}
          />
        </label>
        <label
          htmlFor={`${idPrefix}-quantity`}
          className="grid gap-1 text-xs font-medium"
        >
          Quantity
          <Input
            id={`${idPrefix}-quantity`}
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            value={line.quantity ?? ""}
            onChange={(event) => onChange({ quantity: event.target.value })}
            placeholder="2.50"
            disabled={disabled}
          />
        </label>
        <label
          htmlFor={`${idPrefix}-unit`}
          className="grid gap-1 text-xs font-medium"
        >
          Unit
          <Input
            id={`${idPrefix}-unit`}
            value={line.unit ?? ""}
            onChange={(event) => onChange({ unit: event.target.value })}
            placeholder="count, gram, hour…"
            disabled={disabled}
          />
        </label>
        <label
          htmlFor={`${idPrefix}-unit-price`}
          className="grid gap-1 text-xs font-medium"
        >
          Unit price (USD)
          <Input
            id={`${idPrefix}-unit-price`}
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={line.unit_price ?? ""}
            onChange={(event) => onChange({ unit_price: event.target.value })}
            placeholder="0.00"
            disabled={disabled}
          />
        </label>
        {showTax ? (
          <label
            htmlFor={`${idPrefix}-tax`}
            className="grid gap-1 text-xs font-medium"
          >
            Tax
            <TaxRatePicker
              id={`${idPrefix}-tax`}
              value={line.tax_rate_id ?? ""}
              onChange={(value) => onChange({ tax_rate_id: value })}
              disabled={disabled}
              ariaLabel={`Tax rate for line ${index + 1}`}
            />
          </label>
        ) : null}
        <label
          htmlFor={`${idPrefix}-amount`}
          className="grid gap-1 text-xs font-medium"
        >
          Amount (USD)
          <Input
            id={`${idPrefix}-amount`}
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={line.amount}
            onChange={(event) => onChange({ amount: event.target.value })}
            placeholder="0.00"
            required
            disabled={disabled}
          />
        </label>
        <div className="flex items-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            disabled={disabled || !canRemove}
            aria-label={`Remove line ${index + 1}`}
          >
            Remove
          </Button>
        </div>
      </div>
      <p className="text-xs text-[var(--muted-foreground)]">
        Quantity and unit price are reference details; the amount stays the
        saved line total.
      </p>
      {mismatch ? (
        <p
          className="text-xs text-[var(--muted-foreground)]"
          data-testid={`${idPrefix}-amount-hint`}
        >
          qty × price ={" "}
          {formatMoney({ cents: computedAmount, currency: "USD" })}; amount
          stays authoritative.
        </p>
      ) : null}
      <details className="rounded-lg border border-[var(--border)] px-3 py-2">
        <summary className="cursor-pointer text-xs font-medium text-[var(--muted-foreground)]">
          Product details (optional)
        </summary>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label
            htmlFor={`${idPrefix}-sku`}
            className="grid gap-1 text-xs font-medium"
          >
            SKU ID (optional)
            <Input
              id={`${idPrefix}-sku`}
              value={line.sku_id ?? ""}
              onChange={(event) => onChange({ sku_id: event.target.value })}
              placeholder="SKU UUID"
              disabled={disabled}
            />
          </label>
        </div>
      </details>
    </div>
  );
}
